import { IconName } from '@blueprintjs/icons';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { FormatNumberOptions, FormattedMessage, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import { Icon, majorScale, minorScale, Spinner } from '.';
import { defaultFills } from './Colors';

export interface IInputGroupProps extends BoxProps<'input'> {
  fill?: boolean;
  isInvalid?: boolean;
  validationMessage?: string;
  isLoading?: boolean;
  iconRight?: IconName;
  iconLeft?: IconName;
  iconColor?: string;
}

// TODO: Forward ref
export const InputGroup: React.FC<IInputGroupProps> = forwardRef<
  HTMLInputElement,
  IInputGroupProps
>(
  (
    {
      isLoading = false,
      iconRight,
      iconLeft,
      iconColor = defaultFills.muted,
      fill = true,
      isInvalid = false,
      name,
      validationMessage = '',
      ...props
    },
    ref
  ) => {
    return (
      <Box position="relative">
        <Box
          width={fill ? '100%' : undefined}
          margin={0}
          marginBottom={minorScale(1)}
          {...props}
          ref={ref}
          is="input"
          name={name}
          className={classNames('input-group', isInvalid && 'invalid')}
          style={{
            textIndent: iconLeft ? '24px' : undefined,
          }}
        />
        {validationMessage && (
          <Box
            marginLeft={minorScale(1)}
            className={classNames('p small muted', isInvalid && 'danger')}
            marginBottom={minorScale(1)}
          >
            {validationMessage}
          </Box>
        )}
        {isLoading && (
          <Spinner size={16} position="absolute" right={12} top="10px" />
        )}
        {!isLoading && iconRight && (
          <Icon
            icon={iconRight}
            color={iconColor}
            size={16}
            position="absolute"
            right={12}
            top="9px"
          />
        )}
        {!isLoading && iconLeft && (
          <Icon
            icon={iconLeft}
            color={iconColor}
            size={16}
            position="absolute"
            left={9}
            top="9px"
          />
        )}
      </Box>
    );
  }
);

export const NumberInput: React.FC<
  {
    defaultValue?: number | null;
    value?: number | null;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    minimumSignificantDigits?: number;
    maximumSignificantDigits?: number;
    minimumIntegerDigits?: number;
    onChange?: (n: number) => void;
    min?: number;
    max?: number;
    numberStyle?: FormatNumberOptions['style'];
  } & Omit<IInputGroupProps, 'onChange'>
> = ({
  fill = true,
  value,
  defaultValue,
  numberStyle = 'decimal',
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  minimumSignificantDigits,
  maximumSignificantDigits,
  minimumIntegerDigits,
  onChange,
  isInvalid = false,
  validationMessage = '',
  max,
  min,
  ...props
}) => {
  const intl = useIntl();
  const parseLocaleNumber = (str: string) => {
    // Detect the user's locale decimal separator:
    const decimalSeparator = intl.formatNumber(1.1).substring(1, 2);
    // Detect the user's locale thousand separator:
    let thousandSeparator = intl.formatNumber(1000).substring(1, 2);
    // In case there are locales that don't use a thousand separator
    if (thousandSeparator.match(/\d/)) thousandSeparator = '';

    const val = str
      .replaceAll(thousandSeparator, '')
      .replaceAll(decimalSeparator, '.');
    return parseFloat(val);
  };

  const formatNumber = useCallback(
    (number: number = 0) => {
      let formatted = intl.formatNumber(number, {
        style: numberStyle,
        maximumFractionDigits,
        minimumFractionDigits,
        minimumSignificantDigits,
        maximumSignificantDigits,
        minimumIntegerDigits,
      });
      if (formatted === '-0') {
        formatted = '0';
      }
      return formatted;
    },
    [
      intl,
      numberStyle,
      maximumFractionDigits,
      minimumFractionDigits,
      minimumSignificantDigits,
      maximumSignificantDigits,
      minimumIntegerDigits,
    ]
  );

  const [changed, setChanged] = useState(false);
  const [innerValue, setInnerValue] = useState(
    value === undefined || value === null
      ? defaultValue === undefined || defaultValue === null
        ? ''
        : formatNumber(defaultValue)
      : formatNumber(value)
  );

  useEffect(() => {
    setInnerValue(
      value === undefined || value === null
        ? defaultValue === undefined || defaultValue === null
          ? ''
          : formatNumber(defaultValue)
        : formatNumber(value)
    );
  }, [defaultValue, formatNumber, value]);

  return (
    <>
      <Box
        margin={0}
        marginBottom={minorScale(1)}
        width="100%"
        {...props}
        autoComplete="off"
        autoCorrect="off"
        textAlign="right"
        type="text"
        value={innerValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setInnerValue(e.target.value);
          setChanged(true);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          if (!changed) return;
          let n = 0;
          if (e.target.value) n = parseLocaleNumber(e.target.value);
          if (isNaN(n)) {
            setInnerValue(formatNumber(value || defaultValue));
          } else {
            if (max !== undefined && n > max) n = max as number;
            if (min !== undefined && n < min) n = min as number;
            setInnerValue(formatNumber(n));
            changed && onChange && onChange(n);
          }
          setChanged(false);
        }}
        is="input"
        className={classNames('input-group font-mono', isInvalid && 'invalid')}
      />
      {validationMessage && (
        <Box
          marginLeft={minorScale(1)}
          className={classNames('p small muted', isInvalid && 'danger')}
        >
          {validationMessage}
        </Box>
      )}
    </>
  );
};

export const EmptyInputGroup: React.FC<BoxProps<any>> = (props) => (
  <Box
    backgroundColor="white"
    alignItems="center"
    display="flex"
    height="34px"
    {...props}
    className={classNames(props.className, 'input-group')}
  />
);

export const ControlGroup: React.FC<BoxProps<any> & { fill?: boolean }> = ({
  fill = true,
  ...props
}) => (
  <Box
    {...props}
    className={classNames(props.className, 'control-group')}
    width={fill ? '100%' : undefined}
  />
);

export interface ITextAreaProps extends BoxProps<'textarea'> {
  fill?: boolean;
  hideHint?: boolean;
  isInvalid?: boolean;
  validationMessage?: string;
}

export const TextArea: React.FC<ITextAreaProps> = forwardRef(
  (
    {
      fill = true,
      maxLength,
      hideHint = false,
      isInvalid = false,
      validationMessage = '',
      name,
      ...props
    },
    ref
  ) => {
    const [remaining, setRemaining] = useState(
      (maxLength || 0) - ((props.value || props.defaultValue || '') + '').length
    );

    return (
      <>
        <Box
          width={fill ? '100%' : undefined}
          margin={0}
          marginBottom={minorScale(1)}
          rows={3}
          resize="vertical"
          {...props}
          name={name}
          is="textarea"
          className={classNames('input-group', isInvalid && 'invalid')}
          ref={ref}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (maxLength) {
              e.target.value = e.target.value.slice(0, maxLength);
              setRemaining(maxLength - e.target.value.length);
            }
            props.onChange && props.onChange(e);
          }}
        />
        {((maxLength && !hideHint) || validationMessage) && (
          <Box
            marginLeft="auto"
            className={classNames('p small muted', isInvalid && 'danger')}
          >
            {validationMessage ? (
              validationMessage
            ) : (
              <FormattedMessage
                defaultMessage="{remaining, plural, =1 {# character} other {# characters}} remaining"
                values={{ remaining }}
              />
            )}
          </Box>
        )}
      </>
    );
  }
);

export const Label: React.FC<BoxProps<'label'>> = (props) => (
  <Box is="label" display="block" lineHeight="20px" {...props} />
);

export interface IFormFieldProps extends BoxProps<any> {
  label: string;
  hint?: string | boolean;
  hintRight?: string;
  inline?: boolean;
  center?: boolean;
  isInvalid?: boolean;
  large?: boolean;
  medium?: boolean;
}

export const FormField: React.FC<IFormFieldProps> = ({
  hint,
  hintRight,
  label,
  inline = false,
  children,
  center = false,
  isInvalid = false,
  large = false,
  medium = false,
  ...props
}) => {
  return (
    <Box
      marginBottom={majorScale(3)}
      display={inline ? 'flex' : 'block'}
      alignItems={center ? 'center' : undefined}
      width="100%"
      {...props}
    >
      <Box
        marginRight={inline ? majorScale(1) : 0}
        marginBottom={inline ? 0 : majorScale(1)}
        minWidth={inline ? '44%' : undefined}
        textAlign={inline ? 'right' : 'left'}
        marginTop={inline && !center ? majorScale(1) : 0}
        fontSize={large ? '16px' : undefined}
        fontWeight={large || medium ? 500 : undefined}
      >
        {label}
      </Box>
      <Box display="inline-flex" flexDirection="column" width="100%">
        {children}
        {(hint || hintRight) && (
          <Box
            className={classNames('p small muted', isInvalid && 'danger')}
            marginLeft={minorScale(1)}
            display="flex"
          >
            {hint && (
              <Box width="100%" textAlign="left">
                {hint}
              </Box>
            )}
            {hintRight && (
              <Box width="100%" textAlign="right">
                {hintRight}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
